import { PatientSimple, PatientSimpleQuery } from '@dreryk/egabinet-patient-frontend-api';

import { useCqrsGet } from '@dreryk/command-query-react-common';
import { commonTranslationKeys, translateText, useFormController } from '@dreryk/react-common';
import {
   EmailAddressInput,
   LoaderSpinner,
   Message,
   MessageType,
   Notification,
   NotificationSize,
   NotificationType,
   PhoneNumberInput,
   StyledFlex,
} from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { translationKeys } from '../translations';

import {
   MedicalDocumentationShareFormBean,
   MedicalDocumentationShareFormController,
} from './MedicalDocumentationShareForm.types';

type MedicalDocumentationShareFormRenderProps = Pick<
   MedicalDocumentationShareFormController,
   'handleSubmit' | 'trigger' | 'getValues'
>;

type MedicalDocumentationShareFormBaseProps = Readonly<{
   children: (renderProps: MedicalDocumentationShareFormRenderProps) => JSX.Element;
   patient: PatientSimple;
   phoneNumberHelperText?: string;
}>;

export const MedicalDocumentationShareFormBase = (props: MedicalDocumentationShareFormBaseProps): JSX.Element => {
   const { register, handleSubmit, trigger, getValues } = useFormController<MedicalDocumentationShareFormBean>({
      emailAddress: props.patient.emailAddress ?? null,
      phoneNumber: props.patient.phoneNumber ?? null,
   });

   return (
      <StyledFlex as={'form'} noValidate gap={16} direction={'column'}>
         <EmailAddressInput
            controller={register('emailAddress', {
               required: true,
            })}
            testId={'emailAddress'}
            label={translationKeys.email}
            placeholder={translateText(translationKeys.fillPatientEmail)}
         />
         <StyledFlex gap={8} direction={'column'}>
            <PhoneNumberInput
               controller={register('phoneNumber', {
                  required: true,
               })}
               testId={'phoneNumber'}
               label={translationKeys.phoneNumber}
            />
            <Notification
               testId={'smsPasswordInformation'}
               type={NotificationType.INFO}
               label={
                  props.phoneNumberHelperText ??
                  translationKeys.smsWithPasswordToEncryptedDocumentsWillBeSentToThisNumber
               }
               size={NotificationSize.SMALLER}
            />
         </StyledFlex>

         {props.children({
            trigger: trigger,
            handleSubmit: handleSubmit,
            getValues: getValues,
         })}
      </StyledFlex>
   );
};

type MedicalDocumentationShareFormProps = Readonly<{
   children: (renderProps: MedicalDocumentationShareFormRenderProps) => JSX.Element;
   patientId: number;
   phoneNumberHelperText?: string;
}>;

export const MedicalDocumentationShareForm = (props: MedicalDocumentationShareFormProps): JSX.Element => {
   const { data: patient, error: patientError, fetching: patientFetching } = useCqrsGet<PatientSimple>(() => {
      const query = new PatientSimpleQuery();
      query.id = Slot.of(props.patientId);
      return query;
   });

   if (patientFetching) {
      return <LoaderSpinner />;
   }

   if (patientError) {
      return (
         <Message
            testId={'fetchError'}
            type={MessageType.NEGATIVE}
            label={commonTranslationKeys.unexpectedErrorHasOccurred}
            displayIndicator
         />
      );
   }

   return (
      <MedicalDocumentationShareFormBase patient={patient} phoneNumberHelperText={props.phoneNumberHelperText}>
         {props.children}
      </MedicalDocumentationShareFormBase>
   );
};
